import { useCallback, useEffect, useState, type ReactNode } from 'react';

const SEVERITY_CLASS: Record<string, string> = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFORMATIONAL: 'info',
  UNTRIAGED: 'info',
  UNKNOWN: 'info',
};

export function SeverityBadge({ value }: { value: string }) {
  return <span className={`badge ${SEVERITY_CLASS[value] ?? 'info'}`}>{value}</span>;
}

const STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'high',
  COMPLETED: 'ok',
  IN_PROGRESS: 'medium',
  FAILED: 'critical',
  STOPPED: 'info',
  STOPPING: 'info',
  RESOLVED: 'ok',
  ACCEPTED: 'low',
  FALSE_POSITIVE: 'info',
  SUCCESSFUL: 'ok',
};

export function StatusBadge({ value }: { value: string }) {
  return <span className={`badge ${STATUS_CLASS[value] ?? 'info'}`}>{value.replace(/_/g, ' ')}</span>;
}

/**
 * App statuses need their own mapping: an ACTIVE finding is bad but an ACTIVE app
 * is healthy, so reusing StatusBadge here would colour it as a problem.
 */
const APP_STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'ok',
  CREATING: 'medium',
  DELETING: 'info',
  FAILED: 'critical',
};

export function AppStatusBadge({ value }: { value: string }) {
  return <span className={`badge ${APP_STATUS_CLASS[value] ?? 'info'}`}>{value.toLowerCase()}</span>;
}

/**
 * The masthead mark: a beam sweeping out from a lighthouse, in the brand gradient.
 * Inline rather than a file so it inherits the page and needs no extra request.
 */
export function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
      <defs>
        <linearGradient id="lh-grad" x1="0" y1="32" x2="32" y2="0">
          <stop offset="0%" stopColor="#0f63d6" />
          <stop offset="100%" stopColor="#2ac9ee" />
        </linearGradient>
        <clipPath id="lh-disc">
          <circle cx="16" cy="16" r="16" />
        </clipPath>
      </defs>
      <circle cx="16" cy="16" r="16" fill="url(#lh-grad)" />
      {/* Beam first, so the tower sits on top of it. Clipped to the disc, or its
          corners spill onto the masthead. */}
      <path d="M15 12 32 4.5v15L15 12Z" fill="#fff" opacity="0.45" clipPath="url(#lh-disc)" />
      <path d="M13.2 25 14.6 11h2.8L18.8 25h-5.6Z" fill="#04101f" />
      <circle cx="16" cy="8.6" r="2.9" fill="#fff" />
    </svg>
  );
}

/** The dark banner at the top of a view, carrying the headline. */
export function Hero({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <section className="hero">
      <h1>{title}</h1>
      {children ? <p>{children}</p> : null}
    </section>
  );
}

export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function Metric({ value, note }: { value: number | string; note?: string }) {
  return (
    <>
      <div className="metric">{value}</div>
      {note ? <div className="metric-note">{note}</div> : null}
    </>
  );
}

/** Severity counts shown as a row of labelled numbers, highest severity first. */
export function SeverityCounts({ counts, order }: { counts: Record<string, number>; order: string[] }) {
  const present = order.filter((key) => (counts[key] ?? 0) > 0);

  if (present.length === 0) return <p className="muted">No findings.</p>;

  return (
    <div className="severity-bar">
      {present.map((key) => (
        <div key={key} className="severity-chip">
          <strong>{counts[key]}</strong>
          <SeverityBadge value={key} />
        </div>
      ))}
    </div>
  );
}

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'error' | 'warn'; children: ReactNode }) {
  return <div className={`notice ${kind === 'info' ? '' : kind}`}>{children}</div>;
}

export function formatDate(value?: string): string {
  if (!value) return '—';

  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

interface AsyncState<T> {
  data?: T;
  error?: string;
  loading: boolean;
}

/**
 * Minimal data-fetching hook. The portal is read-mostly and every view loads a
 * single endpoint, so this is enough without pulling in a query library.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ loading: true });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });

    load()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ error: error instanceof Error ? error.message : String(error), loading: false });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload };
}

export function AsyncBoundary<T>({
  state,
  children,
}: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
}) {
  if (state.loading) return <p className="muted">Loading…</p>;
  if (state.error) return <Notice kind="error">{state.error}</Notice>;
  if (state.data === undefined) return <p className="muted">No data.</p>;

  return <>{children(state.data)}</>;
}
